/**
 * `GroupService`（v3.9）—— 群组生命周期：建群 / 邀请 / 退群 / 成员同步 / 实时订阅。
 *
 * 群消息收发由 `ChatService` 负责（逐成员加密信封 + GROUP_REQ publish + 收信入库）；
 * 本服务只管群元数据（本地 localStorage `mesh_groups_v1` 持久化 + 中继权威成员表）。
 */
import type { Libp2pBrowserPeer } from '@/net/peer';
import { useGroupStore, loadGroups, persistGroups } from '@/store/group';
import { useChatStore } from '@/store/chat';
import type { IdentityService } from './IdentityService';
import type { MeshGroup, MeshGroupLocal } from '@shared/types';
import { createLogger } from '@/lib/logger';
import { MeshLinkError } from '@/lib/result';

const logger = createLogger('service:group');

export interface GroupServiceOptions {
  peer: Libp2pBrowserPeer;
  identity: IdentityService;
}

/** 联系人名（群内 sender 显示用）。 */
export function groupMemberName(peerId: string): string {
  const contact = useChatStore.getState().contacts.find((c) => c.peerId === peerId);
  return contact?.name || `好友_${peerId.slice(-4)}`;
}

export class GroupService {
  private started = false;
  private readonly subscribed = new Set<string>();

  constructor(private readonly opts: GroupServiceOptions) {}

  /** 启动：本地群回灌 + 全部群订阅（群事件 → 成员表刷新）。幂等。 */
  start(): void {
    if (this.started) return;
    this.started = true;
    const groups = loadGroups();
    useGroupStore.getState().setGroups(groups);
    for (const g of groups) this.subscribe(g.id);
  }

  stop(): void {
    this.started = false;
    for (const gid of this.subscribed) {
      this.opts.peer.groupUnsubscribe(gid);
    }
    this.subscribed.clear();
  }

  private subscribe(groupId: string): void {
    if (this.subscribed.has(groupId)) return;
    this.subscribed.add(groupId);
    this.opts.peer.groupSubscribe(groupId, (msg) => {
      if (msg.type !== 'group_event' || !msg.group) return;
      const g = msg.group as MeshGroup;
      const local = useGroupStore.getState().groups.find((x) => x.id === g.id);
      if (local) {
        useGroupStore.getState().upsert({ ...local, members: g.members, name: g.name });
      }
    });
  }

  /** 建群（creator=自己）：中继创建 → 本地入库 + 订阅。成员身份统一用 peerId（与在线目录/群消息路由一致）。 */
  async create(name: string): Promise<MeshGroupLocal> {
    const raw = name.trim();
    if (!raw) throw new MeshLinkError('E_INVALID_INPUT', '群名不能为空');
    const pid = this.opts.identity.peerId;
    if (!pid) throw new MeshLinkError('E_NOT_IMPLEMENTED', '身份未就绪');
    const group = (await this.opts.peer.groupCreate(raw, pid)) as MeshGroup;
    const local: MeshGroupLocal = { ...group, unread: 0, muted: false };
    useGroupStore.getState().upsert(local);
    this.subscribe(group.id);
    return local;
  }

  /** 邀请好友入群（去重由中继做）；返回最新成员表。 */
  async invite(groupId: string, peerIds: string[]): Promise<MeshGroup> {
    const group = (await this.opts.peer.groupInvite(groupId, peerIds)) as MeshGroup;
    const local = useGroupStore.getState().groups.find((x) => x.id === groupId);
    if (local) useGroupStore.getState().upsert({ ...local, members: group.members, name: group.name });
    return group;
  }

  /** 退出群：中继移除成员 → 本地移除 + 取消订阅。 */
  async leave(groupId: string): Promise<void> {
    const did = this.opts.identity.current?.did ?? '';
    if (!did) return;
    await this.opts.peer.groupLeave(groupId, did);
    useGroupStore.getState().remove(groupId);
    this.opts.peer.groupUnsubscribe(groupId);
    this.subscribed.delete(groupId);
  }

  /** 拉取群成员表（校正本地）。 */
  async refresh(groupId: string): Promise<void> {
    try {
      const group = (await this.opts.peer.groupMembers(groupId)) as MeshGroup;
      const local = useGroupStore.getState().groups.find((x) => x.id === groupId);
      if (local) useGroupStore.getState().upsert({ ...local, members: group.members, name: group.name });
      else useGroupStore.getState().upsert({ ...group, unread: 0, muted: false });
      this.subscribe(groupId);
    } catch (err) {
      logger.warn('拉群失败', err instanceof Error ? err.message : err);
    }
  }

  /** 打开群会话：置活跃 + 清未读。 */
  open(groupId: string): void {
    useGroupStore.getState().setActive(groupId);
    useGroupStore.getState().markRead(groupId);
  }
}

export { persistGroups };
